<template>
  <div class="pwa-payroll">
    <!-- 헤더 -->
    <div class="payroll-header">
      <div class="header-row">
        <span class="page-title">급여</span>
        <button class="ym-btn" @click="showYearMonthSheet = true">
          {{ formatYearMonth(selectedYearMonth) }}
          <v-icon size="16">mdi-chevron-down</v-icon>
        </button>
      </div>
      <!-- 탭 -->
      <div class="tabs">
        <button :class="{ active: activeTab === 'yobo' }" @click="activeTab = 'yobo'">요양보호사</button>
        <button :class="{ active: activeTab === 'staff' }" @click="activeTab = 'staff'">센터장·사회복지사</button>
      </div>
    </div>

    <!-- 요약 -->
    <div class="summary-bar">
      <div class="summary-item">
        <span class="label">{{ currentList.length }}명</span>
        <span class="value">{{ formatCurrency(summaryData.totalNet) }}</span>
      </div>
      <div class="magam-chip" :class="{ closed: isMagamClosed }">
        {{ isMagamClosed ? '마감완료' : '진행중' }}
      </div>
    </div>

    <!-- 급여 리스트 -->
    <div class="payroll-list">
      <!-- 로딩 -->
      <template v-if="isLoading">
        <div v-for="n in 8" :key="n" class="payroll-item skeleton">
          <div class="skeleton-avatar"></div>
          <div class="skeleton-text"></div>
          <div class="skeleton-amount"></div>
        </div>
      </template>

      <!-- 빈 상태 -->
      <div v-else-if="currentList.length === 0" class="empty-state">
        <v-icon size="48" color="#d1d5db">mdi-currency-krw</v-icon>
        <span class="empty-title">급여 데이터가 없습니다</span>
        <span class="empty-sub">{{ formatYearMonth(selectedYearMonth) }} 급여 정보가 아직 등록되지 않았습니다</span>
      </div>

      <!-- 리스트 -->
      <template v-else>
        <div
          v-for="item in currentList"
          :key="item.emp_code"
          class="payroll-item"
          @click="openPayrollDetail(item)"
        >
          <div class="emp-avatar" :class="getJobColor(item.emp_job)">
            {{ item.emp_name?.charAt(0) }}
          </div>
          <div class="emp-info">
            <span class="name">{{ item.emp_name }}</span>
            <span class="job">{{ item.emp_job || (activeTab === 'yobo' ? '요양보호사' : '') }}</span>
          </div>
          <div class="pay-amount">{{ formatCurrency(item.ewi_net_pay || 0) }}</div>
        </div>
      </template>
    </div>

    <!-- 급여 상세 바텀시트 -->
    <div class="sheet-overlay" :class="{ open: showDetailSheet }" @click="showDetailSheet = false"></div>
    <div class="sheet detail-sheet" :class="{ open: showDetailSheet }">
      <div class="sheet-handle"></div>
      <div class="sheet-header">
        <span>급여 상세</span>
        <button @click="showDetailSheet = false"><v-icon size="20">mdi-close</v-icon></button>
      </div>
      <div class="sheet-body" v-if="selectedPayroll">
        <div class="detail-top">
          <div class="avatar" :class="getJobColor(selectedPayroll.emp_job)">
            {{ selectedPayroll.emp_name?.charAt(0) }}
          </div>
          <div class="info">
            <span class="name">{{ selectedPayroll.emp_name }}</span>
            <span class="job">{{ selectedPayroll.emp_job || '요양보호사' }}</span>
          </div>
        </div>

        <!-- 근무 정보 -->
        <div class="detail-group" v-if="selectedPayroll.ewi_suga_hours">
          <div class="group-title">근무 정보</div>
          <div class="row">
            <span>수가시간</span>
            <span>{{ selectedPayroll.ewi_suga_hours }}시간</span>
          </div>
          <div class="row" v-if="selectedPayroll.ewi_service_days">
            <span>근무일수</span>
            <span>{{ selectedPayroll.ewi_service_days }}일</span>
          </div>
        </div>

        <!-- 지급 -->
        <div class="detail-group">
          <div class="group-title">지급 내역</div>
          <div class="row" v-if="selectedPayroll.ewi_basic_pay">
            <span>기본급</span>
            <span>{{ formatCurrency(selectedPayroll.ewi_basic_pay) }}</span>
          </div>
          <div class="row" v-if="selectedPayroll.ewi_weekly_pay">
            <span>주휴수당</span>
            <span>{{ formatCurrency(selectedPayroll.ewi_weekly_pay) }}</span>
          </div>
          <div class="row" v-if="selectedPayroll.ewi_annual_pay">
            <span>연차수당</span>
            <span>{{ formatCurrency(selectedPayroll.ewi_annual_pay) }}</span>
          </div>
          <div class="row" v-if="selectedPayroll.ewi_meal_pay">
            <span>식대</span>
            <span>{{ formatCurrency(selectedPayroll.ewi_meal_pay) }}</span>
          </div>
          <div class="row" v-if="selectedPayroll.ewi_holiday_pay">
            <span>휴일수당</span>
            <span>{{ formatCurrency(selectedPayroll.ewi_holiday_pay) }}</span>
          </div>
          <div class="row" v-if="selectedPayroll.ewi_overtime_pay">
            <span>연장수당</span>
            <span>{{ formatCurrency(selectedPayroll.ewi_overtime_pay) }}</span>
          </div>
          <div class="row" v-if="selectedPayroll.ewi_night_pay">
            <span>야간수당</span>
            <span>{{ formatCurrency(selectedPayroll.ewi_night_pay) }}</span>
          </div>
          <div class="row" v-if="selectedPayroll.ewi_long_service_pay">
            <span>장기근속수당</span>
            <span>{{ formatCurrency(selectedPayroll.ewi_long_service_pay) }}</span>
          </div>
          <div class="row" v-if="selectedPayroll.ewi_bosu_edu_pay">
            <span>보수교육비</span>
            <span>{{ formatCurrency(selectedPayroll.ewi_bosu_edu_pay) }}</span>
          </div>
          <div class="row" v-if="selectedPayroll.ewi_severe_care_addition">
            <span>중증가산</span>
            <span>{{ formatCurrency(selectedPayroll.ewi_severe_care_addition) }}</span>
          </div>
          <div class="row total">
            <span>지급 합계</span>
            <span class="blue">{{ formatCurrency(selectedPayroll.ewi_total_pay || 0) }}</span>
          </div>
        </div>

        <!-- 공제 -->
        <div class="detail-group">
          <div class="group-title">공제 내역</div>
          <div class="row" v-if="selectedPayroll.ewi_emp_yeon">
            <span>국민연금</span>
            <span class="red">-{{ formatCurrency(selectedPayroll.ewi_emp_yeon) }}</span>
          </div>
          <div class="row" v-if="selectedPayroll.ewi_emp_gun">
            <span>건강보험</span>
            <span class="red">-{{ formatCurrency(selectedPayroll.ewi_emp_gun) }}</span>
          </div>
          <div class="row" v-if="selectedPayroll.ewi_emp_jang">
            <span>장기요양보험</span>
            <span class="red">-{{ formatCurrency(selectedPayroll.ewi_emp_jang) }}</span>
          </div>
          <div class="row" v-if="selectedPayroll.ewi_emp_go">
            <span>고용보험</span>
            <span class="red">-{{ formatCurrency(selectedPayroll.ewi_emp_go) }}</span>
          </div>
          <div class="row" v-if="selectedPayroll.ewi_emp_so">
            <span>소득세</span>
            <span class="red">-{{ formatCurrency(selectedPayroll.ewi_emp_so) }}</span>
          </div>
          <div class="row" v-if="selectedPayroll.ewi_emp_ji">
            <span>지방소득세</span>
            <span class="red">-{{ formatCurrency(selectedPayroll.ewi_emp_ji) }}</span>
          </div>
          <div class="row total">
            <span>공제 합계</span>
            <span class="red">-{{ formatCurrency(selectedPayroll.ewi_emp_total_deduction || 0) }}</span>
          </div>
        </div>

        <!-- 실지급액 -->
        <div class="net-pay-card">
          <span class="label">실지급액</span>
          <span class="amount">{{ formatCurrency(selectedPayroll.ewi_net_pay || 0) }}</span>
        </div>
      </div>
    </div>

    <!-- 연월 선택 바텀시트 -->
    <div class="sheet-overlay" :class="{ open: showYearMonthSheet }" @click="showYearMonthSheet = false"></div>
    <div class="sheet ym-sheet" :class="{ open: showYearMonthSheet }">
      <div class="sheet-handle"></div>
      <div class="sheet-header">
        <span>연월 선택</span>
        <button @click="showYearMonthSheet = false"><v-icon size="20">mdi-close</v-icon></button>
      </div>
      <div class="sheet-body">
        <div class="year-selector">
          <button class="arrow-btn" @click="yearMonth.year--">
            <v-icon size="24">mdi-chevron-left</v-icon>
          </button>
          <span class="year-text">{{ yearMonth.year }}년</span>
          <button class="arrow-btn" @click="yearMonth.year++">
            <v-icon size="24">mdi-chevron-right</v-icon>
          </button>
        </div>
        <div class="month-grid">
          <button
            v-for="m in 12"
            :key="m"
            class="month-btn"
            :class="{ active: yearMonth.month === m, disabled: isFutureMonth(m) }"
            :disabled="isFutureMonth(m)"
            @click="selectMonth(m)"
          >
            {{ m }}월
          </button>
        </div>
      </div>
    </div>

    <LodingComponent :is-visible="isLoading" text="급여 정보를 불러오는 중..." />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import axios from 'axios';
import LodingComponent from '@/components/loading/LodingComponent.vue';

const store = useStore();
const route = useRoute();
const user = computed(() => store.state.user);

const isLoading = ref(false);
const activeTab = ref<'yobo' | 'staff'>('yobo');
const showDetailSheet = ref(false);
const showYearMonthSheet = ref(false);
const selectedPayroll = ref<any>(null);

// localStorage 키
const STORAGE_KEY = 'pwa_payroll_yearMonth';

// 저장된 연월 또는 지난달 기본값
const getSavedYearMonth = () => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && saved.length === 6) {
    return {
      year: parseInt(saved.substring(0, 4)),
      month: parseInt(saved.substring(4, 6))
    };
  }
  // 기본값: 지난달
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { year: lastMonth.getFullYear(), month: lastMonth.getMonth() + 1 };
};

const yearMonth = ref(getSavedYearMonth());

// 연월 변경 시 localStorage에 저장
const saveYearMonth = () => {
  const ym = `${yearMonth.value.year}${String(yearMonth.value.month).padStart(2, '0')}`;
  localStorage.setItem(STORAGE_KEY, ym);
};
const selectedYearMonth = computed(() => 
  `${yearMonth.value.year}${String(yearMonth.value.month).padStart(2, '0')}`
);

const payrollData = ref<any[]>([]);
const isMagamClosed = ref(false);

// 탭에 따라 필터링
const currentList = computed(() => {
  if (activeTab.value === 'yobo') {
    return payrollData.value.filter(item => item.emp_job === '요양보호사');
  } else {
    return payrollData.value.filter(item => 
      item.emp_job === '센터장' || item.emp_job === '사회복지사'
    );
  }
});

const summaryData = computed(() => {
  const list = currentList.value;
  return {
    totalCount: list.length,
    totalNet: list.reduce((sum, item) => sum + (parseInt(item.ewi_net_pay) || 0), 0)
  };
});

// 급여 데이터 조회
const fetchPayrollData = async () => {
  if (!user.value?.m_sisul_code) {
    console.warn('사용자 정보가 없습니다.');
    return;
  }
  
  try {
    isLoading.value = true;
    const ym = selectedYearMonth.value;
    const year = ym.substring(0, 4);
    const sisulCode = user.value.m_sisul_code;

    // 마감 상태 확인
    try {
      const magamResponse = await axios.get(
        `/api/calc/wage-magam-yearly/${sisulCode}/${year}`,
        { withCredentials: true }
      );
      if (magamResponse.data.success) {
        const magamData = magamResponse.data.data || [];
        isMagamClosed.value = magamData.some(
          (m: any) => m.wm_ym === ym && m.wm_job_type === '전체합계' && m.wm_isuse === 'y'
        );
      }
    } catch (e) {
      console.error('마감 상태 조회 오류:', e);
    }

    // 급여 데이터 조회 (hh_emp_sabok_work_info 테이블에서)
    const response = await axios.get('/api/calc/yobo-payroll', {
      params: {
        sisulCode: sisulCode,
        yearMonth: ym
      },
      withCredentials: true
    });

    if (response.data.success) {
      payrollData.value = response.data.data || [];
    } else {
      payrollData.value = [];
    }
  } catch (error) {
    console.error('급여 데이터 조회 오류:', error);
    payrollData.value = [];
    store.commit('showSnackbar', {
      text: '급여 데이터를 불러오지 못했습니다.',
      color: 'error'
    });
  } finally {
    isLoading.value = false;
  }
};

// 유틸리티
const formatYearMonth = (ym: string) => {
  if (!ym || ym.length !== 6) return '';
  return `${ym.substring(0, 4)}년 ${parseInt(ym.substring(4, 6))}월`;
};

const formatCurrency = (value: number) => {
  if (!value) return '0원';
  return new Intl.NumberFormat('ko-KR').format(value) + '원';
};

const getJobColor = (job?: string) => {
  if (job === '요양보호사') return 'yobo';
  if (job === '사회복지사') return 'sabok';
  if (job === '센터장') return 'sisul';
  return 'default';
};

const isFutureMonth = (month: number) => {
  const now = new Date();
  if (yearMonth.value.year > now.getFullYear()) return true;
  if (yearMonth.value.year === now.getFullYear() && month > now.getMonth() + 1) return true;
  return false;
};

const selectMonth = (month: number) => {
  yearMonth.value.month = month;
  saveYearMonth(); // localStorage에 저장
  showYearMonthSheet.value = false;
  fetchPayrollData();
};

const openPayrollDetail = (item: any) => {
  selectedPayroll.value = item;
  showDetailSheet.value = true;
};

// URL 쿼리에서 ym 가져오기 또는 localStorage에서 복원
onMounted(() => {
  const ymParam = route.query.ym as string;
  if (ymParam && ymParam.length === 6) {
    yearMonth.value = {
      year: parseInt(ymParam.substring(0, 4)),
      month: parseInt(ymParam.substring(4, 6))
    };
    saveYearMonth(); // URL 파라미터도 저장
  }
  // getSavedYearMonth()에서 이미 localStorage 값을 불러왔으므로 바로 조회
  fetchPayrollData();
});

// 연도 변경 시 localStorage 저장 (월은 selectMonth에서 저장)
watch(() => yearMonth.value.year, () => {
  saveYearMonth();
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/fonts.scss';

.pwa-payroll {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
  font-family: $Noto_Sans_KR !important;
  * { font-family: $Noto_Sans_KR !important; }

  // 헤더
  .payroll-header {
    position: sticky;
    top: 0;
    z-index: 100;
    background: #fff;
    padding-top: env(safe-area-inset-top, 0px);

    .header-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 16px 12px;

      .page-title { font-size: 22px; font-weight: 700; }
      
      .ym-btn {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 8px 12px;
        background: #f0f4ff;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        color: #1e3a8a;
      }
    }

    .tabs {
      display: flex;
      padding: 0 16px;
      border-bottom: 1px solid #f0f0f0;

      button {
        flex: 1;
        padding: 12px 0;
        background: none;
        border: none;
        border-bottom: 2px solid transparent;
        font-size: 14px;
        color: #999;

        &.active {
          color: #1e3a8a;
          border-bottom-color: #1e3a8a;
          font-weight: 600;
        }
      }
    }
  }

  // 요약
  .summary-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: #fff;
    margin: 8px 12px;
    border-radius: 10px;

    .summary-item {
      .label { font-size: 13px; color: #888; margin-right: 8px; }
      .value { font-size: 18px; font-weight: 700; color: #111; }
    }

    .magam-chip {
      padding: 5px 12px;
      border-radius: 14px;
      font-size: 12px;
      font-weight: 600;
      background: #fef3c7;
      color: #b45309;

      &.closed { background: #d1fae5; color: #047857; }
    }
  }

  // 급여 리스트
  .payroll-list {
    padding: 0 12px;

    .payroll-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px;
      background: #fff;
      border-radius: 10px;
      margin-bottom: 8px;
      cursor: pointer;

      &:active { background: #fafafa; }

      &.skeleton {
        .skeleton-avatar { 
          width: 42px; 
          height: 42px; 
          border-radius: 50%; 
          background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }
        .skeleton-text { 
          height: 16px; 
          width: 80px; 
          background: #f0f0f0; 
          border-radius: 4px; 
        }
        .skeleton-amount { 
          height: 16px; 
          width: 70px; 
          background: #f0f0f0; 
          border-radius: 4px; 
          margin-left: auto; 
        }
      }

      .emp-avatar {
        width: 42px;
        height: 42px;
        border-radius: 50%;
        color: #fff;
        font-size: 15px;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        &.yobo { background: linear-gradient(135deg, #1e3a8a, #3b82f6); }
        &.sabok { background: linear-gradient(135deg, #059669, #10b981); }
        &.sisul { background: linear-gradient(135deg, #7c3aed, #a78bfa); }
        &.default { background: linear-gradient(135deg, #64748b, #94a3b8); }
      }

      .emp-info {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;

        .name { font-size: 15px; font-weight: 600; color: #111; }
        .job { font-size: 12px; color: #888; margin-top: 2px; }
      }

      .pay-amount {
        font-size: 15px;
        font-weight: 700;
        color: #1e3a8a;
      }
    }
  }

  // 빈 상태
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 60px 20px;
    background: #fff;
    margin: 12px;
    border-radius: 12px;

    .empty-title {
      font-size: 16px;
      font-weight: 600;
      color: #666;
      margin-top: 12px;
    }

    .empty-sub {
      font-size: 13px;
      color: #999;
      margin-top: 4px;
    }
  }

  // 바텀 시트 공통 (z-index를 네비게이션 바(1000)보다 높게)
  .sheet-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.4);
    z-index: 1100;
    opacity: 0;
    visibility: hidden;
    transition: all 0.25s;
    &.open { opacity: 1; visibility: visible; }
  }

  .sheet {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: #fff;
    border-radius: 16px 16px 0 0;
    z-index: 1101;
    transform: translateY(100%);
    transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
    overflow: hidden;

    &.open { transform: translateY(0); }

    .sheet-handle {
      width: 36px;
      height: 4px;
      background: #ddd;
      border-radius: 2px;
      margin: 8px auto;
    }

    .sheet-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      border-bottom: 1px solid #f0f0f0;
      font-size: 17px;
      font-weight: 700;

      button {
        padding: 4px;
        background: #f5f5f5;
        border-radius: 50%;
        border: none;
      }
    }

    .sheet-body {
      padding: 16px;
      overflow-y: auto;
    }

    &.detail-sheet {
      max-height: 85vh;
      .sheet-body { max-height: calc(85vh - 60px); }
    }

    &.ym-sheet {
      max-height: 50vh;
      .sheet-body { max-height: calc(50vh - 60px); }
    }
  }

  // 상세 시트
  .detail-top {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-bottom: 16px;
    margin-bottom: 16px;
    border-bottom: 1px solid #f0f0f0;

    .avatar {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      color: #fff;
      font-size: 18px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;

      &.yobo { background: linear-gradient(135deg, #1e3a8a, #3b82f6); }
      &.sabok { background: linear-gradient(135deg, #059669, #10b981); }
      &.sisul { background: linear-gradient(135deg, #7c3aed, #a78bfa); }
      &.default { background: linear-gradient(135deg, #64748b, #94a3b8); }
    }

    .info {
      display: flex;
      flex-direction: column;
      .name { font-size: 18px; font-weight: 700; }
      .job { font-size: 13px; color: #888; margin-top: 2px; }
    }
  }

  .detail-group {
    margin-bottom: 16px;
    background: #f9f9f9;
    border-radius: 10px;
    padding: 14px;

    .group-title {
      font-size: 12px;
      font-weight: 600;
      color: #888;
      margin-bottom: 10px;
    }

    .row {
      display: flex;
      justify-content: space-between;
      padding: 8px 0;
      font-size: 14px;
      border-bottom: 1px solid #f0f0f0;

      &:last-child { border-bottom: none; }

      &.total {
        padding-top: 12px;
        margin-top: 4px;
        font-weight: 600;
        border-top: 1px solid #e0e0e0;
        border-bottom: none;
      }

      .blue { color: #1e3a8a; }
      .red { color: #ef4444; }
    }
  }

  .net-pay-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 18px;
    background: linear-gradient(135deg, #1e3a8a, #3b82f6);
    border-radius: 12px;
    color: #fff;

    .label { font-size: 15px; font-weight: 500; }
    .amount { font-size: 22px; font-weight: 700; }
  }

  // 연월 선택 시트
  .year-selector {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    margin-bottom: 20px;

    .arrow-btn {
      width: 40px;
      height: 40px;
      background: #f5f5f5;
      border: none;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #333;

      &:active { background: #e8e8e8; }
    }

    .year-text {
      font-size: 20px;
      font-weight: 700;
      color: #111;
      min-width: 80px;
      text-align: center;
    }
  }

  .month-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;

    .month-btn {
      padding: 14px 0;
      border: 1px solid #e0e0e0;
      border-radius: 10px;
      background: #fff;
      font-size: 15px;
      font-weight: 500;
      color: #333;

      &:active:not(.disabled) { background: #f5f5f5; }

      &.active {
        background: #1e3a8a;
        border-color: #1e3a8a;
        color: #fff;
        font-weight: 600;
      }

      &.disabled {
        background: #f9f9f9;
        color: #ccc;
        cursor: not-allowed;
      }
    }
  }
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
